import {HttpClient, HttpResponse} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {defaultOptions, HttpOptionsBuilder, pdfOptions, responseToObject} from '@emma-labs/shared/angular';
import {ArchivedProposal, ENVIRONMENT, EnvironmentBroker} from '@emma-labs/shared/typescript';
import {catchError, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {RevisionAnswers} from '../../entities/archived-proposal-object/revision-answers';
import {handleHttpError} from '../../utils/utility-constants';
import {AuthService} from '../auth.service';

@Injectable({
  providedIn: 'root',
})
export class ArchivedProposalApiService {
  private readonly basePath: string;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    @Inject(ENVIRONMENT) readonly environment: EnvironmentBroker,
  ) {
    this.basePath = `${environment.msSaleApiBase}/archived-proposals`;
  }

  getById(sessionId: string, archivedProposalId: string): Observable<ArchivedProposal> {
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(archivedProposalId)}`);

    return this.http.get(url.toString(), options).pipe(
      map((response) => responseToObject<ArchivedProposal>(response as HttpResponse<string>)),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  answerHealthAnswersRevisions(
    sessionId: string,
    archivedProposalId: string,
    revisionsAnswers: RevisionAnswers[],
  ): Observable<ArchivedProposal> {
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(archivedProposalId)}/health-answers-revisions`);

    return this.http.put(url.toString(), {healthAnswersRevisionsAnswers: revisionsAnswers}, options).pipe(
      map((response) => responseToObject<ArchivedProposal>(response as HttpResponse<string>)),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  uploadHealthAnswersRevisionDocuments(
    sessionId: string,
    archivedProposalId: string,
    healthAnswersRevisionId: string,
    documents: File[],
  ): Observable<ArchivedProposal> {
    const options = new HttpOptionsBuilder().withApiKey(sessionId).buildOptions();
    const url = new URL(
      `${this.basePath}/${btoa(archivedProposalId)}/${btoa(healthAnswersRevisionId)}/documents`,
    );
    const body = new FormData();
    documents.forEach((document) => body.append('files', document));

    return this.http.post(url.toString(), body, options).pipe(
      map((response) => responseToObject<ArchivedProposal>(response as HttpResponse<string>)),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  removeHealthAnswersRevisionDocument(
    sessionId: string,
    archivedProposalId: string,
    healthAnswersRevisionId: string,
    documentName: string,
  ): Observable<ArchivedProposal> {
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(
      `${this.basePath}/${btoa(archivedProposalId)}/${btoa(healthAnswersRevisionId)}/${documentName}`,
    );

    return this.http.delete(url.toString(), options).pipe(
      map((response) => responseToObject<ArchivedProposal>(response as HttpResponse<string>)),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  downloadHealthAnswersRevisionDocument(
    sessionId: string,
    archivedProposalId: string,
    healthAnswersRevisionId: string,
    documentName: string,
  ): Observable<Blob> {
    const options = pdfOptions(sessionId).buildOptions();
    const url = new URL(
      `${this.basePath}/${btoa(archivedProposalId)}/${btoa(
        healthAnswersRevisionId,
      )}/documents/${documentName}`,
    );

    return this.http.get(url.toString(), {...options, responseType: 'blob'}).pipe(
      map((res) => res.body as Blob),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  completeHealthAnswersRevisions(
    sessionId: string,
    archivedProposalId: string,
    ids: string[],
  ): Observable<ArchivedProposal> {
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(archivedProposalId)}/health-answers-revisions/complete`);

    return this.http.put(url.toString(), {ids}, options).pipe(
      map((response) => responseToObject<ArchivedProposal>(response as HttpResponse<string>)),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }
}
